/**
 * Gradient utilities for generating sophisticated layered gradients.
 * Used by HeroLogoGradient and ReferenceGrid cards.
 */

// Import pre-calculated gradients
import staticGradients from '@/theme/tokens/generated-gradients.json';

/**
 * AQ Brand gradient colors - the primary 6 brand colors
 * Used for fallback gradients when no image is available
 */
export const AQ_BRAND_GRADIENT_COLORS = [
  '#041246', // Navy
  '#f26649', // Orange
  '#232eff', // Electric Blue
  '#ffffff', // White
  '#c7eafb', // Sky Blue
  '#000000', // Black
];

// In-memory cache for layered gradients
// Prevents regenerating large gradient strings for the same input colors
const layeredGradientCache = new Map<string, string>();

/**
 * Generate sophisticated layered gradients mixing radials, ellipses, and linears.
 * Creates an organic, wave-like effect with depth and movement.
 */
export const generateLayeredGradients = (colors: string[] | null | undefined): string => {
  if (!colors?.length) return 'none';

  // Create a cache key from the colors array
  const cacheKey = colors.join('|');

  // Return cached result if available
  if (layeredGradientCache.has(cacheKey)) {
    return layeredGradientCache.get(cacheKey)!;
  }

  const layers: string[] = [];

  // Each color gets multiple gradient layers for depth
  // Alpha values are in hex (00-FF)
  // Primary (index 1) and Accent (index 2) get boosted opacity
  // Primary: lower-left emphasis, Accent: upper-right emphasis
  colors.forEach((color, i) => {
    const offset = i * 17; // Prime number for varied distribution
    const isPrimary = i === 1;
    const isAccent = i === 2;
    const isFeatured = isPrimary || isAccent;

    // Opacity multipliers - primary and accent get 1.25x boost (accessibility-first)
    const baseAlpha = isFeatured ? '30' : '25';  // 48 vs 37
    const waveAlpha = isFeatured ? '1e' : '18';  // 30 vs 24
    const linearAlpha = isFeatured ? '14' : '10'; // 20 vs 16
    const spotAlpha = isFeatured ? '3c' : '30';  // 60 vs 48

    // Position offsets for featured colors
    // Primary: lower-left (low x, high y), Accent: upper-right (high x, low y)
    // Background variations (indices 3-8): scattered across canvas
    const scatteredPositions = [
      { x: 50, y: 50, wx: 30, wy: 40, sx: 60, sy: 55, angle: 180 },   // center (index 3: -20%)
      { x: 85, y: 65, wx: 95, wy: 50, sx: 90, sy: 70, angle: 315 },   // right-mid (index 4: +20%)
      { x: 20, y: 35, wx: 5, wy: 45, sx: 15, sy: 40, angle: 90 },     // left-upper (index 5: -10%)
      { x: 65, y: 90, wx: 70, wy: 95, sx: 55, sy: 85, angle: 270 },   // center-bottom (index 6: +10%)
      { x: 40, y: 20, wx: 45, wy: 10, sx: 35, sy: 25, angle: 135 },   // left-top (index 7: -5%)
      { x: 90, y: 40, wx: 100, wy: 35, sx: 80, sy: 45, angle: 0 },    // right-upper (index 8: +5%)
    ];
    const scatterIdx = i - 3;
    const scatter = scatterIdx >= 0 && scatterIdx < scatteredPositions.length ? scatteredPositions[scatterIdx] : null;

    const xBase = isPrimary ? 10 : isAccent ? 75 : scatter ? scatter.x : 15 + offset;
    const yBase = isPrimary ? 70 : isAccent ? 15 : scatter ? scatter.y : 20 + (i * 12);
    const waveX = isPrimary ? -10 : isAccent ? 80 : scatter ? scatter.wx : -20 + (i * 25);
    const waveY = isPrimary ? 85 : isAccent ? 25 : scatter ? scatter.wy : 60 + (i * 8);
    const spotX = isPrimary ? 25 : isAccent ? 85 : scatter ? scatter.sx : 70 - (i * 10);
    const spotY = isPrimary ? 80 : isAccent ? 20 : scatter ? scatter.sy : 30 + (i * 15);
    const linearAngle = isPrimary ? 45 : isAccent ? 225 : scatter ? scatter.angle : 135 + (i * 30);

    // Large soft radial bloom
    layers.push(
      `radial-gradient(ellipse 80% 50% at ${xBase}% ${yBase}%, ${color}${baseAlpha} 0%, transparent 50%)`
    );

    // Sweeping wave using stretched ellipse
    layers.push(
      `radial-gradient(ellipse 150% 40% at ${waveX}% ${waveY}%, ${color}${waveAlpha} 0%, transparent 70%)`
    );

    // Diagonal linear accent
    if (i % 2 === 0 || isFeatured) {
      layers.push(
        `linear-gradient(${linearAngle}deg, transparent 30%, ${color}${linearAlpha} 50%, transparent 70%)`
      );
    }

    // Small intense highlight spots
    layers.push(
      `radial-gradient(circle at ${spotX}% ${spotY}%, ${color}${spotAlpha} 0%, transparent 25%)`
    );
  });

  // Add flowing wave overlays
  layers.push(
    `radial-gradient(ellipse 200% 80% at 0% 100%, ${colors[0]}15 0%, transparent 50%)`,
    `radial-gradient(ellipse 180% 60% at 100% 0%, ${colors[Math.min(1, colors.length - 1)]}12 0%, transparent 60%)`,
    `linear-gradient(160deg, transparent 0%, ${colors[Math.min(2, colors.length - 1)]}08 30%, transparent 60%)`
  );

  const result = layers.join(', ');

  // Cache the result
  // Limit cache size to 50 entries to prevent memory leaks
  if (layeredGradientCache.size >= 50) {
    const firstKey = layeredGradientCache.keys().next().value;
    if (firstKey !== undefined) {
      layeredGradientCache.delete(firstKey);
    }
  }
  layeredGradientCache.set(cacheKey, result);

  return result;
};

/**
 * Get a pre-calculated gradient for a scheme if it exists.
 * This avoids expensive runtime calculation for standard brand schemes.
 */
export const getStaticGradient = (schemeName: string): string | undefined => {
  // @ts-expect-error - The JSON import might be typed as generic JSON
  return staticGradients[schemeName];
};

/**
 * Hero logo gradient data that can be passed to child components.
 */
export interface HeroLogoGradientData {
  variant: 'heroLogoGradient';
  gradientColors: string[];
  heroLogo: {
    asset: { _ref?: string; _id?: string };
    [key: string]: unknown;
  };
}

/**
 * Check if hero data is of the LogoGradient type with required fields.
 * heroLogo is a Sanity image (asset-based) — Cloudinary's `public_id` is gone.
 */
export const isHeroLogoGradient = (hero: unknown): hero is HeroLogoGradientData => {
  if (!hero || typeof hero !== 'object') return false;
  const h = hero as Record<string, unknown>;
  if (h.variant !== 'heroLogoGradient') return false;
  if (!Array.isArray(h.gradientColors) || h.gradientColors.length === 0) return false;
  if (!h.heroLogo || typeof h.heroLogo !== 'object') return false;
  const logo = h.heroLogo as Record<string, unknown>;
  return typeof logo.asset === 'object' && logo.asset !== null;
};
